<script setup lang="ts">
import Button from 'primevue/button'
import { useI18n } from 'vue-i18n'

import LocationHeader from '@/components/location/LocationHeader.vue'
import SavedAddressList from '@/components/location/SavedAddressList.vue'
import type { SavedAddress } from '@/composables/useAddresses'

/**
 * Экран «Куда доставить?» со списком сохранённых адресов. Один и тот же и в
 * онбординге, и в диалоге с главной: чаще всего клиент заказывает туда же,
 * куда и в прошлый раз, и гонять его через карту ради этого незачем.
 */
interface Props {
  addresses: SavedAddress[]
  /** Идёт проверка выбранной точки — кнопка «Добавить адрес» ждёт */
  saving?: boolean
  /** Карандаш правки: в онбординге править нечем, там только выбор */
  editable?: boolean
}

withDefaults(defineProps<Props>(), { saving: false, editable: true })

const emit = defineEmits<{
  select: [address: SavedAddress]
  edit: [address: SavedAddress]
  add: []
  back: []
}>()

const { t } = useI18n()
</script>

<template>
  <LocationHeader :title="t('address.whereTo')" @back="emit('back')" />

  <div class="saved-screen">
    <section class="saved-screen__card">
      <h2 class="saved-screen__title">{{ t('address.title') }}</h2>
      <SavedAddressList
        :addresses="addresses"
        :editable="editable"
        @select="emit('select', $event)"
        @edit="emit('edit', $event)"
      />
    </section>
  </div>

  <footer class="saved-screen__footer">
    <Button
      :label="t('address.add')"
      :loading="saving"
      class="saved-screen__cta"
      @click="emit('add')"
    />
  </footer>
</template>

<style scoped lang="scss">
.saved-screen {
  overflow: hidden auto;
  flex: 1;
  min-height: 0;
  margin-top: 4px;

  &__card {
    padding: var(--screen-padding);
    border-radius: var(--radius-xl);
    background: var(--surface-card);
  }

  &__title {
    margin-bottom: 10px;
    font-size: 24px;
    font-weight: var(--fw-bold);
    line-height: 37px;
  }

  /* Панель действия снизу — как на экране адресов */
  &__footer {
    padding: var(--screen-padding);
    padding-bottom: calc(var(--screen-padding) + var(--tg-safe-area-bottom));
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
    background: var(--surface-card);
  }

  &__cta {
    width: 100%;
  }
}
</style>
